'use client';

import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { createClient } from '@/lib/supabase/client';

export type MemberRole = 
  | 'superusuario' 
  | 'mesa_directiva' 
  | 'tesorero' 
  | 'finanzas' 
  | 'administracion' 
  | 'socio' 
  | 'dama';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: MemberRole;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface AuthContextType {
  user: any;
  session: any;
  loading: boolean;
  userRole: MemberRole | null;
  isActive: boolean;
  canManageMembers: boolean;
  userProfile: UserProfile | null;
  allowedModules: string[];
  allowedActions: string[];
  hasPermission: (moduleKey: string) => boolean;
  hasAction: (moduleKey: string, actionKey: string) => boolean;
  refreshPermissions: () => Promise<void>; // 🌟 Permite refrescar el Sidebar tras guardar cambios
  signUp: (email: string, password: string, metadata?: any) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<any>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<UserProfile | null>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<MemberRole | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [allowedModules, setAllowedModules] = useState<string[]>([]);
  const [allowedActions, setAllowedActions] = useState<string[]>([]);

  // 🌟 FIX 1: Instancia fija y única de Supabase
  const supabase = useMemo(() => createClient(), []);

  // Consultar perfil de usuario desde 'user_profiles'
  const fetchUserProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, role, is_active, created_at, updated_at')
        .eq('id', userId)
        .single();

      if (error || !data) {
        console.error('[AUTH] Perfil no encontrado en user_profiles:', error?.message);
        return null;
      }
      return data as UserProfile;
    } catch (err) {
      console.error('[AUTH] Excepción al consultar perfil:', err);
      return null;
    }
  }, [supabase]);

  // Cargar módulos y subpermisos para el rol
  const loadPermissionsForRole = useCallback(async (role: MemberRole | null) => {
    if (!role) {
      setAllowedModules([]);
      setAllowedActions([]);
      return;
    }

    if (role === 'superusuario') {
      setAllowedModules([
        'dashboard_social',
        'proyectos',
        'socios',
        'comites',
        'finanzas',
        'calendario',
        'pase_lista',
        'evidencias',
        'educacion',
        'usuarios',
      ]);
      setAllowedActions([]);
      return;
    }

    try {
      const [modulesRes, actionsRes] = await Promise.all([
        supabase.from('role_module_permissions').select('module_key').eq('role_key', role),
        supabase.from('role_action_permissions').select('module_key, action_key').eq('role_key', role),
      ]);

      setAllowedModules(modulesRes.data ? modulesRes.data.map((item: any) => item.module_key) : []);
      setAllowedActions(actionsRes.data ? actionsRes.data.map((item: any) => `${item.module_key}:${item.action_key}`) : []);
    } catch {
      setAllowedModules([]);
      setAllowedActions([]);
    }
  }, [supabase]);

  // 🌟 FIX 2: Función de carga de sesión con actualización atómica limpia
  const loadProfileForSession = useCallback(async (sessionUser: any) => {
    if (!sessionUser) {
      setUserProfile(null);
      setUserRole(null);
      setIsActive(false);
      setAllowedModules([]);
      setAllowedActions([]);
      return;
    }

    const profile = await fetchUserProfile(sessionUser.id);
    if (profile) {
      setUserProfile(profile);
      setUserRole(profile.role as MemberRole);
      setIsActive(profile.is_active);
      await loadPermissionsForRole(profile.role as MemberRole);
    } else {
      console.warn(`[AUTH] No existe registro en user_profiles para el ID ${sessionUser.id}`);
      setUserProfile(null);
      setUserRole(null);
      setIsActive(false);
      setAllowedModules([]);
      setAllowedActions([]);
    }
  }, [fetchUserProfile, loadPermissionsForRole]);

  // 🌟 FIX 3: Event Listener limpio sin condiciones de carrera
  useEffect(() => {
    let isMounted = true;

    // Supabase Auth listener oficial (maneja restauración de sesión automática)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, currentSession) => {
      if (!isMounted) return;

      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        await loadProfileForSession(currentSession.user);
      } else {
        setUserProfile(null);
        setUserRole(null);
        setIsActive(false);
        setAllowedModules([]);
        setAllowedActions([]);
      }

      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [supabase, loadProfileForSession]);

  const refreshPermissions = async () => {
    if (userRole) {
      await loadPermissionsForRole(userRole);
    }
  };

  const canManageMembers = userRole === 'superusuario' || userRole === 'mesa_directiva';

  const hasPermission = (moduleKey: string): boolean => {
    if (userRole === 'superusuario') return true;
    return allowedModules.includes(moduleKey);
  };

  const hasAction = (moduleKey: string, actionKey: string): boolean => {
    if (userRole === 'superusuario') return true;
    return allowedActions.includes(`${moduleKey}:${actionKey}`);
  };

  const signUp = async (email: string, password: string, metadata: any = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;

    if (data.user) {
      const profile = await fetchUserProfile(data.user.id);
      if (profile && profile.is_active === false) {
        await supabase.auth.signOut();
        throw new Error('Tu cuenta está suspendida. Contacta a la Mesa Directiva.');
      }
    }

    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const getCurrentUser = async () => {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  const getUserProfile = async (): Promise<UserProfile | null> => {
    if (!user) return null;
    return fetchUserProfile(user.id);
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    userRole,
    isActive,
    canManageMembers,
    userProfile,
    allowedModules,
    allowedActions,
    hasPermission,
    hasAction,
    refreshPermissions,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};